"use client";

import {
  Plate,
  PlateContent,
  useEditorContainerRef,
  useEditorRef,
  usePlateEditor,
} from "platejs/react";
import { MarkdownPlugin, defaultRules } from "@platejs/markdown";

import { useCallback, useEffect, useRef, useState } from "react";
import Toolbar, { ToolbarGroup } from "./toolbar/Toolbar";
import styles from "./styles/SlateEditor.module.scss";
import classNames from "classnames";
import FontAwesome from "../utilities/FontAwesome";
import { markdownDefaultMaxLength, MarkType, NodeType } from "./utils";
import { basePlateComponentOverrides } from "./plugins/base-plate-component-overrides";
import { basePlatePlugins } from "./plugins/base-plate-plugins";
import { BlockButton } from "./toolbar/buttons/BlockButton";
import { ListButton } from "./toolbar/buttons/ListButton";
import { MarkButton } from "./toolbar/buttons/MarkButton";
import { LinkButton } from "./toolbar/buttons/LinkButton";
import { RedoButton, UndoButton } from "./toolbar/buttons/HistoryButtons";
import { FloatingToolbar } from "./toolbar/FloatingToolbar";

export const defaultValue = [
  {
    type: NodeType.PARAGRAPH,
    children: [{ text: "" }],
  },
];

function EditorContainer(props) {
  const editor = useEditorRef();
  const containerRef = useEditorContainerRef();

  return <div id={editor.uid} ref={containerRef} {...props} />;
}

const MarkdownEditor = (props: {
  value: string;
  className?: string;
  editorClassName?: string;
  handleTextChange: (value: string, editor) => void;
  placeholder?: string;
  maxLength?: number;
  editable?: boolean;
}) => {
  const { maxLength = markdownDefaultMaxLength, editable = true } = props;
  const [editInMarkdown, setEditInMarkdown] = useState(false);

  const editor = usePlateEditor({
    value: (editor) => {
      if (props.value) {
        // Safely access Markdown API once the editor exists
        editor.children = editor
          .getApi(MarkdownPlugin)
          .markdown.deserialize(props.value);
      } else {
        editor.children = defaultValue;
      }

      // Must return something even if redundant
      return editor.children;
    },
    // prevent overriding default max length to a longer limit - default is more than enough and is in place
    // to prevent bogging down the system when converting markdown to HTML
    maxLength: Math.min(maxLength, markdownDefaultMaxLength),
    plugins: [
      ...basePlatePlugins,
      MarkdownPlugin.configure({
        options: {
          rules: {
            list: {
              deserialize: (mdastNode, deco, options) => {
                // solution to the possibility of empty lists while hopefully https://github.com/udecode/plate/issues/4485 is addressed
                // @ts-ignore
                const deserialized = defaultRules.list.deserialize(
                  mdastNode,
                  deco,
                  options,
                );

                deserialized.children.forEach((child) => {
                  if (child.type === "li" && child.children.length === 0) {
                    child.children = [
                      {
                        type: "lic",
                        children: [{ text: "" }],
                      },
                    ];
                  }
                });

                return deserialized;
              },
            },
          },
        },
      }),
    ],
    override: {
      components: basePlateComponentOverrides,
    },
  });

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const slateEditorRef = useRef<HTMLDivElement | null>(null);

  function forceUpdateState(md) {
    editor.tf.reset(); // important to fix issue of lag between value changing and editor adjusting selection, see https://github.com/ianstormtaylor/slate/issues/3575
    const deserializedValue = editor.api.markdown.deserialize(md);
    editor.tf.setValue(deserializedValue);
  }

  useEffect(() => {
    const scrollHeight = textareaRef.current?.scrollHeight;
    if (textareaRef.current && scrollHeight) {
      textareaRef.current.style.height = scrollHeight.toString() + "px";
    }
  }, [textareaRef, editInMarkdown]);

  useEffect(() => {
    const editor = slateEditorRef.current;
    if (!editable && editor) {
      // @ts-ignore
      const child_style = editor.children[0].style;
      child_style.minHeight = "0px";
    }
  }, [slateEditorRef]);

  const handleTextChange = useCallback(() => {
    const cleanedText = editor.api.markdown
      .serialize()
      .replace(/[\u200B-\u200D\uFEFF]/g, "");
    props.handleTextChange(cleanedText, editor);
  }, []);

  const containerClassName = classNames({
    [styles.container]: true,
    [styles.containerRawMarkdown]: editInMarkdown,
    notranslate: true,
    [props.className || ""]: props.className,
  });

  const editorClassName = classNames({
    [styles.editor]: true,
    [props.editorClassName || ""]: props.editorClassName,
  });

  return (
    <div className={containerClassName}>
      {editInMarkdown ? (
        <div className={editorClassName}>
          <textarea
            value={props.value}
            rows={editable ? 8 : undefined}
            ref={textareaRef}
            maxLength={maxLength}
            onChange={(e) => {
              forceUpdateState(e.target.value);
              props.handleTextChange(e.target.value, editor);
            }}
            disabled={!editable}
            style={editable ? {} : { resize: "none" }}
          />
        </div>
      ) : (
        <Plate editor={editor} onValueChange={handleTextChange}>
          <EditorContainer className={styles.content}>
            {editable && (
              <Toolbar>
                <ToolbarGroup>
                  <UndoButton />
                  <RedoButton />
                </ToolbarGroup>
                <ToolbarGroup>
                  <BlockButton
                    format={NodeType.HEADING_THREE}
                    tooltip={"Heading"}
                  >
                    <FontAwesome prefix={"fas"} name={"fa-heading"} />
                  </BlockButton>
                  <ListButton
                    format={NodeType.UNORDERED_LIST}
                    tooltip={"Unordered List"}
                  >
                    <FontAwesome prefix={"fas"} name={"fa-list-ul"} />
                  </ListButton>
                  <ListButton
                    format={NodeType.ORDERED_LIST}
                    tooltip={"Ordered List"}
                  >
                    <FontAwesome prefix={"fas"} name={"fa-list-ol"} />
                  </ListButton>
                  <BlockButton
                    format={NodeType.BLOCK_QUOTE}
                    tooltip={"Blockquote"}
                  >
                    <FontAwesome prefix={"fas"} name={"fa-quote-left"} />
                  </BlockButton>
                  <MarkButton format={MarkType.STRONG} tooltip={"Bold"}>
                    <FontAwesome prefix={"fas"} name={"fa-bold"} />
                  </MarkButton>
                  <MarkButton format={MarkType.EMPHASIS} tooltip={"Italic"}>
                    <FontAwesome prefix={"fas"} name={"fa-italic"} />
                  </MarkButton>
                  <LinkButton showTooltip={true} />
                </ToolbarGroup>
              </Toolbar>
            )}
            <FloatingToolbar>
              <ToolbarGroup>
                <BlockButton format={NodeType.HEADING_THREE}>
                  <FontAwesome prefix={"fas"} name={"fa-heading"} />
                </BlockButton>
                <ListButton format={NodeType.UNORDERED_LIST}>
                  <FontAwesome prefix={"fas"} name={"fa-list-ul"} />
                </ListButton>
                <ListButton format={NodeType.ORDERED_LIST}>
                  <FontAwesome prefix={"fas"} name={"fa-list-ol"} />
                </ListButton>
                <BlockButton format={NodeType.BLOCK_QUOTE}>
                  <FontAwesome prefix={"fas"} name={"fa-quote-left"} />
                </BlockButton>
                <MarkButton format={MarkType.STRONG}>
                  <FontAwesome prefix={"fas"} name={"fa-bold"} />
                </MarkButton>
                <MarkButton format={MarkType.EMPHASIS}>
                  <FontAwesome prefix={"fas"} name={"fa-italic"} />
                </MarkButton>
                <LinkButton showTooltip={false} />
              </ToolbarGroup>
            </FloatingToolbar>
            <div className={editorClassName} ref={slateEditorRef}>
              <PlateContent readOnly={!editable} />
            </div>
          </EditorContainer>
        </Plate>
      )}
      {editable && (
        <>
          <button onClick={() => setEditInMarkdown((v) => !v)}>
            {editInMarkdown
              ? "Edit in Rich Text"
              : "Edit in Markdown (advanced)"}
          </button>
          {editInMarkdown || (
            <span
              className={styles.tooltip}
              data-content={
                "Markdown is a lightweight markup\nlanguage used by SchoolBlocks.\nNote that not all markdown elements\nare supported."
              }
              style={{ marginLeft: ".25rem" }}
            >
              <FontAwesome prefix={"fas"} name={"fa-question-circle"} />
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default MarkdownEditor;
